import React from 'react';
import styled from 'styled-components';
import SubLayout from '../components/layout/SubLayout';
import { promotionMenus } from '../data/promotionMenus';

const Intro = styled.p`
  font-size: 1.05rem;
  color: #555;
  line-height: 1.9;
  margin-bottom: 50px;
`;

const SectionLabel = styled.h3`
  font-size: 1.3rem;
  color: #1a2a40;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
`;

/*
 * 법 조문 인용 블록.
 * 본문 설명과 법령 원문을 시각적으로 구분해, 어디까지가 법에 적힌 내용인지 드러낸다.
 */
const LawQuote = styled.blockquote`
  margin: 0 0 20px;
  padding: 25px 30px;
  background: #f8f9fa;
  border-left: 4px solid #0056b3;
  border-radius: 0 8px 8px 0;

  p {
    font-size: 1rem;
    color: #333;
    line-height: 1.9;
    margin: 0;
  }

  cite {
    display: block;
    margin-top: 12px;
    font-size: 0.85rem;
    font-style: normal;
    color: #888;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.9;
  margin-bottom: 50px;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 50px;
`;

const ServiceCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px 25px;
  background: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 86, 179, 0.1);
    border-color: #0056b3;
  }

  .icon { font-size: 2rem; margin-bottom: 12px; }
  h4 { font-size: 1.1rem; color: #222; margin-bottom: 8px; }
  p { font-size: 0.9rem; color: #666; line-height: 1.7; }
`;

const FeatureBox = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #0056b3;
  padding: 20px 25px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 15px;

  h4 { font-size: 1rem; color: #333; margin-bottom: 6px; }
  p { font-size: 0.9rem; color: #666; line-height: 1.6; }
`;

/* 인증 마크는 글이 아니라 표식이므로 본문 위에 가로로 나란히 놓는다 */
const MarkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
  padding: 25px 30px;
  margin-bottom: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;

  img {
    height: 70px;
    width: auto;
    object-fit: contain;
  }

  .text {
    flex: 1;
    min-width: 200px;
    font-size: 0.95rem;
    color: #555;
    line-height: 1.8;
    strong { color: #1a2a40; }
  }

  @media (max-width: 768px) {
    padding: 20px;
    gap: 18px;
    img { height: 54px; }
  }
`;

const SocialEnterprise = () => {
  return (
    <SubLayout
      title="사회적기업"
      sidebarMenus={promotionMenus}
      category="홍보센터"
      categoryEn="PR Center"
      bannerBg="/images/bg_building.jpg"
    >
      <MarkRow>
        <img src="/images/logo_socialenterprise.png" alt="사회적기업 인증 마크" />
        <div className="text">
          주식회사 두성시스템은 <strong>2011년 12월 의정부시 최초로 사회적기업 인증</strong>을 받았습니다.
          (2011년 11월 예비사회적기업 지정)
        </div>
      </MarkRow>

      <Intro>
        사회적기업은 이윤 추구만을 목적으로 하는 일반 기업과 달리, 영업활동으로 수익을 창출하면서
        그 수익을 취약계층 일자리 제공과 지역사회 공헌이라는 사회적 목적에 다시 사용하는 기업입니다.
        두성시스템은 건물종합관리와 전문건설업을 수행하며 지역 내 일자리를 만들고,
        그 성과를 지역사회에 환원하는 것을 경영의 중심에 두고 있습니다.
      </Intro>

      <SectionLabel>정의</SectionLabel>
      <LawQuote>
        <p>
          &ldquo;사회적기업&rdquo;이란 취약계층에게 사회서비스 또는 일자리를 제공하거나 지역사회에 공헌함으로써
          지역주민의 삶의 질을 높이는 등의 사회적 목적을 추구하면서 재화 및 서비스의 생산·판매 등
          영업활동을 하는 기업으로서 제7조에 따라 인증받은 자를 말한다.
        </p>
        <cite>— 사회적기업 육성법 제2조 제1호</cite>
      </LawQuote>
      <Description>
        핵심은 <strong>사회적 목적</strong>과 <strong>영업활동</strong>을 동시에 갖춘다는 점입니다.
        사회적 목적만 추구하면 비영리단체이고, 영업활동만 하면 일반 기업입니다.
        사회적기업은 두 가지를 함께 수행하며, 고용노동부 장관의 인증을 거쳐야만 사회적기업이라는 명칭을 쓸 수 있습니다.
      </Description>

      <SectionLabel>목적</SectionLabel>
      <LawQuote>
        <p>
          이 법은 사회적기업의 설립·운영을 지원하고 사회적기업을 육성하여 우리 사회에서 충분하게 공급되지 못하는
          사회서비스를 확충하고 새로운 일자리를 창출함으로써 사회통합과 국민의 삶의 질 향상에 이바지함을 목적으로 한다.
        </p>
        <cite>— 사회적기업 육성법 제1조</cite>
      </LawQuote>
      <Description>
        시장이 스스로 해결하지 못하는 두 가지 문제, 즉 <strong>부족한 사회서비스</strong>와
        <strong> 취약계층의 일자리</strong>를 기업의 방식으로 풀어내자는 것이 제도의 취지입니다.
        정부 재정으로 직접 지원하는 대신, 지속 가능한 사업 모델을 갖춘 기업을 키워
        그 효과가 계속 이어지도록 하는 데 목적이 있습니다.
      </Description>

      <SectionLabel>사회적기업의 유형</SectionLabel>
      <ServiceGrid>
        <ServiceCard>
          <div className="icon">👷</div>
          <h4>일자리제공형</h4>
          <p>전체 근로자 중 취약계층 고용 비율이 30% 이상인 유형입니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">🤝</div>
          <h4>사회서비스제공형</h4>
          <p>사회서비스를 제공받는 대상 중 취약계층 비율이 30% 이상인 유형입니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">⚖️</div>
          <h4>혼합형</h4>
          <p>취약계층 고용과 사회서비스 제공을 함께 수행하며, 각각 20% 이상인 유형입니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">🏘️</div>
          <h4>지역사회공헌형</h4>
          <p>지역의 인적·물적 자원을 활용하거나 지역 문제 해결에 기여하는 유형입니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">💡</div>
          <h4>창의·혁신형</h4>
          <p>위 유형에 속하지 않으나 사회적 목적 실현이 인정되어 심의를 거친 유형입니다.</p>
        </ServiceCard>
      </ServiceGrid>

      <SectionLabel>인증 요건</SectionLabel>
      <FeatureBox>
        <h4>조직 형태</h4>
        <p>민법상 법인·조합, 상법상 회사·합자조합, 비영리민간단체 등 법에서 정한 조직 형태를 갖춰야 합니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>유급근로자 고용과 영업활동</h4>
        <p>유급근로자를 고용해 재화와 서비스를 생산·판매하는 영업활동을 해야 하며, 그 수입이 일정 기준 이상이어야 합니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>사회적 목적의 실현</h4>
        <p>취약계층에 대한 사회서비스·일자리 제공 또는 지역사회 공헌을 조직의 주된 목적으로 삼아야 합니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>이해관계자가 참여하는 의사결정 구조</h4>
        <p>서비스 수혜자와 근로자 등 이해관계자가 참여하는 의사결정 구조를 갖춰야 합니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>이윤의 사회적 재투자</h4>
        <p>상법상 회사의 경우, 배분 가능한 이윤이 발생하면 그 3분의 2 이상을 사회적 목적을 위해 사용해야 합니다.</p>
      </FeatureBox>
    </SubLayout>
  );
};

export default SocialEnterprise;
